<script setup>
  import { computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Trash, Plus } from "lucide-vue-next";
  import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Checkbox } from "@/components/ui/checkbox";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useMCreativeStore } from "@stores/mCreative";
  import { useMTemplatesStore } from "@stores/mTemplates";
  import { AiTextInput } from "@/components/episto-ui/ai";
  import CreativeAssetsTool from "@/components/MCampaign/Creatives/CreativeAssetsTool.vue";
  import CreativeTemplatesTool from "@/components/MCampaign/Creatives/CreativeTemplatesTool.vue";
  import CreativeVideosTool from "@/components/MCampaign/Creatives/CreativeVideosTool.vue";
  import FacebookPageSelect from "@/components/MCampaign/Creatives/CreativeEdit/FacebookPageSelect.vue";

  const mCreativeStore = useMCreativeStore();
  const { mCreative, currentLocaleId, currentRegion, currentUrlPrefix, currentUrlSuffix } = storeToRefs(mCreativeStore);
  const {
    mediaTypeOptions,
    addCreativeTitle,
    addCreativeSubtitle,
    removeCreativeTitle,
    removeCreativeSubtitle,
    updateCreativeTitle,
    updateCreativeSubtitle,
    updateMediaType,
    updatePlacementFeed,
    updatePlacementStory,
    updateLocaleId,
    updateRegion,
    updateUrlSuffix,
  } = mCreativeStore;

  const mCampaignStore = useMCampaignStore();
  const { localeOptions, regionOptions } = storeToRefs(mCampaignStore);

  const mTemplatesStore = useMTemplatesStore();
  const { setBlankTemplate } = mTemplatesStore;

  const mediaTypeSelectOptions = computed(() =>
    mediaTypeOptions.map((opt) => ({ value: opt.value, label: opt.label })),
  );

  const localeSelectOptions = computed(() =>
    localeOptions.value.map((opt) => ({ value: opt.value, label: `${opt.label} (${opt.variant})` })),
  );

  const regionSelectOptions = computed(() =>
    regionOptions.value.map((opt) => ({ value: opt.value, label: opt.label })),
  );

  watch(
    () => mCreative.value.media_type,
    () => {
      if (mCreative.value.media_type === "video") setBlankTemplate();
    },
  );
</script>

<template>
  <Card class="p-6">
    <CardHeader class="pb-4">
      <CardTitle class="heading-page">{{ mCreative.name || "Test creative" }}</CardTitle>
    </CardHeader>
    <CardContent class="space-y-4">
      <LabeledField label="Name">
        <Input v-model="mCreative.name" />
      </LabeledField>
      <LabeledField label="Label">
        <Input v-model="mCreative.label" />
      </LabeledField>
      <LabeledField label="Medium type">
        <Select
          :model-value="mCreative.media_type"
          :options="mediaTypeSelectOptions"
          @update:model-value="updateMediaType" />
      </LabeledField>
      <LabeledField label="Placement">
        <div class="flex gap-6">
          <label class="flex items-center gap-2">
            <Checkbox
              :model-value="mCreative.placements?.feed"
              :disabled="mCreative.placements?.story === false"
              @update:model-value="updatePlacementFeed" />
            <span class="text-sm">Feed</span>
          </label>
          <label class="flex items-center gap-2">
            <Checkbox
              :model-value="mCreative.placements?.story"
              :disabled="mCreative.placements?.feed === false"
              @update:model-value="updatePlacementStory" />
            <span class="text-sm">Story</span>
          </label>
        </div>
      </LabeledField>

      <FacebookPageSelect v-model="mCreative.facebook_page_external_id" />

      <div v-if="mCreative.media_type === 'image'">
        <h4 class="heading-section mt-4 mb-3">Images</h4>
        <CreativeAssetsTool />
        <CreativeTemplatesTool />
      </div>

      <div v-else>
        <h4 class="heading-section mt-4 mb-3">Videos</h4>
        <CreativeVideosTool
          v-if="mCreative.placements?.feed"
          video-type="feed" />
        <CreativeVideosTool
          v-if="mCreative.placements?.story"
          video-type="story" />
      </div>

      <h4 class="heading-section mt-4 mb-3">Titles</h4>
      <LabeledField label="Primary text">
        <div
          v-for="(title, i) in mCreative.titles || []"
          :key="`title-item-${i}`"
          class="mb-2 flex items-start gap-2">
          <AiTextInput
            :model-value="title"
            type="rich"
            :rows="1"
            show-emoji
            plain-text
            class="flex-1"
            @update:model-value="updateCreativeTitle(i, $event)" />
          <Button
            variant="ghost"
            size="icon-sm"
            @click="removeCreativeTitle(i)">
            <Trash class="h-4 w-4" />
          </Button>
        </div>
        <Button @click="addCreativeTitle()">
          <Plus class="h-4 w-4 mr-1" />
          Add title
        </Button>
      </LabeledField>
      <LabeledField label="Catchphrase">
        <div
          v-for="(title, i) in mCreative.subtitles || []"
          :key="`subtitle-item-${i}`"
          class="mb-2 flex items-start gap-2">
          <AiTextInput
            :model-value="title"
            type="rich"
            :rows="1"
            show-emoji
            plain-text
            class="flex-1"
            @update:model-value="updateCreativeSubtitle(i, $event)" />
          <Button
            variant="ghost"
            size="icon-sm"
            @click="removeCreativeSubtitle(i)">
            <Trash class="h-4 w-4" />
          </Button>
        </div>
        <Button @click="addCreativeSubtitle()">
          <Plus class="h-4 w-4 mr-1" />
          Add subtitle
        </Button>
      </LabeledField>

      <h4 class="heading-section mt-4 mb-3">Redirections</h4>
      <LabeledField label="Locale">
        <Select
          :model-value="currentLocaleId"
          :options="localeSelectOptions"
          @update:model-value="updateLocaleId" />
      </LabeledField>
      <LabeledField label="Country">
        <Select
          :model-value="currentRegion"
          :options="regionSelectOptions"
          @update:model-value="updateRegion" />
      </LabeledField>
      <LabeledField label="URL">
        <div class="flex w-full">
          <span
            class="inline-flex w-[70%] shrink-0 items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground truncate">
            {{ currentUrlPrefix }}
          </span>
          <Input
            :model-value="currentUrlSuffix"
            class="w-[30%] rounded-l-none"
            @update:model-value="updateUrlSuffix" />
        </div>
      </LabeledField>
    </CardContent>
  </Card>
</template>
